import {
  DiffType,
  MosPosComponent,
  MosPosMenu,
  MosPosPricePeriod,
} from '@/services/MosPosMenuMaster';

/** business date 時点のメニュー状態。 */
export type MenuStatus = 'active' | 'ended' | 'scheduled' | 'unknown';

/** 取込差分の絞り込み。1度に1つだけ選べます。 */
export type DiffFilter = 'all' | DiffType;

/**
 * SCR010 No.14 の確認テーブルの絞り込み。
 * すべて／新規／更新／変更なし／単品／セットから1つだけ選べます。
 */
export type PreviewFilter = DiffFilter | MenuCategory;

/**
 * SCR009 No.10 のフィルターピル。
 * すべて／単品／セット／子商品ありから1つだけ選べます（排他選択）。
 */
export type ListFilter = 'all' | MenuCategory | 'hasChildren';

/** 関係種別のタグ。凡例と展開行の両方で同じ色を使うため1か所で持ちます。 */
export type RelationKind = 'included' | 'choice' | 'accessory';

/**
 * 関係種別タグの配色。区分バッジ（単品 teal / セット orange）と紛れないよう、
 * 設計モックの .rtag と同じ purple / blue / yellow 系を使います。
 */
export const RELATION_KIND_STYLE: Record<
  RelationKind,
  { background: string; color: string }
> = {
  included: { background: '#E9D8FD', color: '#553C9A' },
  choice: { background: '#BEE3F8', color: '#2A4365' },
  accessory: { background: '#FEFCBF', color: '#744210' },
};

/**
 * 状態バッジの配色。設計モックの .badge.live / .ended / .prepare と同じ値を使い、
 * サマリカードの配色（販売開始前=amber / 販売中=green / 販売終了=gray）と揃えます。
 * unknown（要確認）はモックに定義が無いため、注意を促す赤（.badge.stop）を割り当てています。
 */
export const MENU_STATUS_STYLE: Record<
  MenuStatus,
  { background: string; borderColor: string; color: string }
> = {
  active: { background: '#F0FFF4', borderColor: '#9AE6B4', color: '#276749' },
  ended: { background: '#EDF2F7', borderColor: '#CBD5E0', color: '#5A6473' },
  scheduled: {
    background: '#FFFAF0',
    borderColor: '#FBD38D',
    color: '#975A16',
  },
  unknown: { background: '#FFF5F5', borderColor: '#FEB2B2', color: '#C53030' },
};

/** SCR009 No.13 の表示件数の選択肢。 */
export const PAGE_SIZES = [20, 30, 50, 100];

/** SCR009 No.13 の既定の表示件数。設計書の「デフォルト20〜30件」に合わせます。 */
export const DEFAULT_PAGE_SIZE = 30;

const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * 商品コード・Web code・商品名にキーワードが含まれるかを判定します。
 * 大文字小文字は区別せず、前後の空白は落とします。
 */
export const matchesKeyword = (menu: MosPosMenu, keyword: string): boolean => {
  const needle = normalize(keyword);
  if (needle === '') return true;

  const haystack = [
    menu.mets_menu_cd,
    menu.mets_screen_menu_name,
    menu.mets_receipt_menu_name ?? '',
    ...menu.web_menu_cds.map((w) => w.web_menu_cd),
  ];
  return haystack.some((value) => normalize(value).includes(needle));
};

/** business date を覆う販売期間を返します。 */
export const coveringPrices = (
  menu: MosPosMenu,
  businessDate: string
): MosPosPricePeriod[] =>
  menu.prices.filter(
    (p) => p.sell_start_date <= businessDate && businessDate <= p.sell_end_date
  );

/**
 * business date 時点のメニュー状態を返します。
 * 判定に使う日付は必ず API が返した business_date です。ブラウザの時計は見ません。
 */
export const menuStatus = (
  menu: MosPosMenu,
  businessDate: string
): MenuStatus => {
  if (menu.prices.length === 0) return 'unknown';

  const covering = coveringPrices(menu, businessDate);
  if (covering.length === 1) {
    return covering[0].sell_stop_flag === 1 ? 'ended' : 'active';
  }
  if (covering.length === 0) {
    const firstStart = menu.prices
      .map((p) => p.sell_start_date)
      .reduce((a, b) => (a < b ? a : b));
    const lastEnd = menu.prices
      .map((p) => p.sell_end_date)
      .reduce((a, b) => (a > b ? a : b));
    if (businessDate < firstStart) {
      // 開始前でも販売停止フラグが立っていれば販売終了。設計書 SCR009 Item#6 の
      // 抽出条件が sell_stop_flag != 1 を要求するため、バックエンドの StatusAt と
      // 同じ判定にしてサマリカードと状態バッジを一致させます。
      const first = menu.prices.find((p) => p.sell_start_date === firstStart);
      if (first?.sell_stop_flag === 1) return 'ended';
      return 'scheduled';
    }
    if (businessDate > lastEnd) return 'ended';
  }
  return 'unknown';
};

/** 状態表示の日本語ラベル。 */
export const menuStatusLabel = (status: MenuStatus): string => {
  switch (status) {
    case 'active':
      return '販売中';
    case 'ended':
      return '販売終了';
    case 'scheduled':
      return '販売開始前';
    default:
      return '要確認';
  }
};

/** SCR009/SCR010 の「区分」列。DB上の内部区分値は出さず単品/セットだけを見せます。 */
export type MenuCategory = 'single' | 'set';

/** Master CSV の menu_type が 3 のときセット。区分はマスタの属性なので導出しません。 */
const MENU_TYPE_SET = 3;

/**
 * 区分を返します。menu_type が入っていればそれを見ます。
 * 旧データなど menu_type が無い場合だけ、セット構成の有無で代替判定します。
 */
export const menuCategory = (menu: MosPosMenu): MenuCategory => {
  if (menu.menu_type !== undefined) {
    return menu.menu_type === MENU_TYPE_SET ? 'set' : 'single';
  }
  return menu.set_configs.length > 0 ? 'set' : 'single';
};

/** 区分の日本語ラベル。 */
export const categoryLabel = (category: MenuCategory): string =>
  category === 'set' ? 'セット' : '単品';

/** 子商品リレーションを持つか。フィルターピル「子商品あり」の判定に使います。 */
export const hasChildren = (menu: MosPosMenu): boolean =>
  menu.source_relation_count > 0;

/** 取込区分の日本語ラベル。 */
export const diffLabel = (diff: DiffType): string => {
  switch (diff) {
    case 'new':
      return '新規登録';
    case 'update':
      return '更新（上書き）';
    default:
      return '変更なし';
  }
};

/** 行バッジ用の短い取込区分ラベル。チップは diffLabel の長い方を使います。 */
export const diffShortLabel = (diff: DiffType): string => {
  switch (diff) {
    case 'new':
      return '新規';
    case 'update':
      return '更新';
    default:
      return '変更なし';
  }
};

/** business date 時点で有効な販売価格。無ければ最初の期間の価格を出します。 */
export const displayPrice = (
  menu: MosPosMenu,
  businessDate: string
): MosPosPricePeriod | undefined => {
  const covering = coveringPrices(menu, businessDate);
  if (covering.length > 0) return covering[0];
  return menu.prices[0];
};

/** YYYY-MM-DD をそのまま表示用に整えます。 */
export const formatDate = (value: string): string => value.replace(/-/g, '/');

/** 終了日がこの値なら無期限とみなし、終了日を表示しません。 */
const OPEN_ENDED_SELL_END_DATE = '2199-12-31';

/**
 * 販売期間。全ての価格期間をまとめた「最小開始日〜最大終了日」を返します。
 * NOS表記に合わせて YYYY/MM/DD、期間が1件も無いメニューはハイフンにします。
 * 終了日が無期限（2199-12-31）のときは「開始日〜」で止めます。
 * 価格期間が複数あるときは、内訳が隠れていることが分かるよう期間数を添えます。
 * 表示は営業日に依存しません（営業日時点の1期間だけを出すと複数期間の存在が分からなくなるため）。
 */
export const salesPeriod = (menu: MosPosMenu): string => {
  if (menu.prices.length === 0) return '-';
  const start = menu.prices
    .map((p) => p.sell_start_date)
    .reduce((a, b) => (a < b ? a : b));
  const end = menu.prices
    .map((p) => p.sell_end_date)
    .reduce((a, b) => (a > b ? a : b));
  const range =
    end === OPEN_ENDED_SELL_END_DATE
      ? `${formatDate(start)}〜`
      : `${formatDate(start)}〜${formatDate(end)}`;
  return menu.prices.length > 1
    ? `${range} (${menu.prices.length}期間)`
    : range;
};

/**
 * SCR009 No.12 のCSVエクスポート。
 *
 * ヘッダーはバックエンドの mosposmenu.MasterCSVHeader / RelationCSVHeader と
 * 完全一致させます。列順が1つずれると再取込が V-002 で弾かれるためです。
 *
 * 注意: エクスポートは materialize 済みの menu graph から復元するため、元CSVと
 * バイト単位で同一にはなりません。web code と価格期間は取込時に別々に重複排除
 * されるので、ここでは組み合わせを総当たりで出します（再取込すると同じ graph に
 * 戻ります）。choice 見出し行の display_order のように graph が保持しない値は
 * 空欄で出します。
 */
export const MASTER_CSV_HEADER = [
  'mets_menu_cd',
  'web_menu_cd',
  'mets_screen_menu_name',
  'mets_receipt_menu_name',
  'menu_type',
  'sell_price',
  'reduced_sell_price',
  'subgrouping_cd',
  'reduced_tax_rate_flag',
  'alcohol_flag',
  'sell_start_date',
  'sell_end_date',
  'sell_stop_flag',
  'special_order_item_codes',
];

export const RELATION_CSV_HEADER = [
  'relation_type',
  'parent_mets_menu_cd',
  'child_mets_menu_cd',
  'set_parent_mets_menu_cd',
  'mets_choice_cd',
  'display_order',
  'quantity',
  'sell_price_difference',
  'reduced_sell_price_difference',
  'sell_quantity',
  'mets_crust_cd',
  'mets_size_cd',
  'child_menu_name',
  'reduced_tax_rate_flag',
];

/** 構成品を表す mets_choice_cd。バックエンドの IncludedChoiceCD と同じ値です。 */
const INCLUDED_CHOICE_CD = '0000';
/** choice 見出し行の child_mets_menu_cd。バックエンドの DummyChildMenuCD と同じ値です。 */
const DUMMY_CHILD_MENU_CD = '000000';

const CSV_EOL = '\r\n';

/** カンマ・引用符・改行を含む値だけ RFC4180 の形で引用します。 */
const escapeCsv = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const cell = (value: string | number | undefined): string =>
  value === undefined ? '' : escapeCsv(String(value));

const toCsv = (header: string[], rows: (string | number | undefined)[][]) =>
  [header.join(','), ...rows.map((r) => r.map(cell).join(','))].join(CSV_EOL);

/** 親商品マスタCSV。web code × 価格期間の組み合わせごとに1行出します。 */
export const buildMasterCsv = (menus: MosPosMenu[]): string => {
  const rows: (string | number | undefined)[][] = [];
  menus.forEach((menu) => {
    // 片方が空でも1行は出したいので、空配列はダミー1件に置き換えます。
    const webMenus =
      menu.web_menu_cds.length > 0
        ? menu.web_menu_cds
        : [{ web_menu_cd: '', special_order_item_codes: [] }];
    const prices =
      menu.prices.length > 0 ? menu.prices : [undefined as never | undefined];

    webMenus.forEach((web) => {
      prices.forEach((price) => {
        rows.push([
          menu.mets_menu_cd,
          web.web_menu_cd,
          menu.mets_screen_menu_name,
          menu.mets_receipt_menu_name ?? '',
          menu.menu_type ?? '',
          price?.sell_price ?? '',
          price?.reduced_sell_price ?? '',
          menu.subgrouping_cd ?? '',
          menu.reduced_tax_rate_flag,
          menu.alcohol_flag ?? '',
          price?.sell_start_date ?? '',
          price?.sell_end_date ?? '',
          price?.sell_stop_flag ?? '',
          web.special_order_item_codes.join(','),
        ]);
      });
    });
  });
  return toCsv(MASTER_CSV_HEADER, rows);
};

/** 子商品リレーションCSV。set / choice / accessory の3種類を復元します。 */
export const buildRelationCsv = (menus: MosPosMenu[]): string => {
  const rows: (string | number | undefined)[][] = [];

  const componentRow = (
    setParent: string,
    choiceCD: string,
    item: MosPosComponent
  ) => [
    'set',
    setParent,
    item.mets_menu_cd,
    setParent,
    choiceCD,
    item.display_order,
    item.quantity,
    item.sell_price_difference ?? '',
    item.reduced_sell_price_difference ?? '',
    item.sell_quantity ?? '',
    item.mets_crust_cd ?? '',
    item.mets_size_cd ?? '',
    item.mets_screen_menu_name,
    item.reduced_tax_rate_flag,
  ];

  menus.forEach((menu) => {
    menu.set_configs.forEach((sc) => {
      const parent = sc.set_parent_mets_menu_cd;
      sc.included_items.forEach((item) => {
        rows.push(componentRow(parent, INCLUDED_CHOICE_CD, item));
      });
      sc.choice_groups.forEach((group) => {
        // 見出し行。取込時は choice_group_name の取得にだけ使われます。
        rows.push([
          'choice',
          parent,
          DUMMY_CHILD_MENU_CD,
          '',
          group.mets_choice_cd,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          group.choice_group_name,
          '',
        ]);
        group.selectable_items.forEach((item) => {
          rows.push(componentRow(parent, group.mets_choice_cd, item));
        });
      });
    });

    menu.accessories.forEach((acc) => {
      rows.push([
        'accessory',
        menu.mets_menu_cd,
        acc.mets_menu_cd,
        '',
        '',
        acc.display_order,
        acc.quantity,
        '',
        '',
        '',
        '',
        '',
        acc.mets_screen_menu_name,
        acc.reduced_tax_rate_flag,
      ]);
    });
  });

  return toCsv(RELATION_CSV_HEADER, rows);
};

/** SCR009 No.9/No.10 の検索とフィルターピルを同時に適用します。 */
export const filterMenus = (
  menus: MosPosMenu[],
  keyword: string,
  filter: ListFilter
): MosPosMenu[] =>
  menus.filter((menu) => {
    if (!matchesKeyword(menu, keyword)) return false;
    switch (filter) {
      case 'single':
      case 'set':
        return menuCategory(menu) === filter;
      case 'hasChildren':
        return hasChildren(menu);
      default:
        return true;
    }
  });

/** 1ページ分を切り出します。範囲外のページは空配列になります。 */
export const paginate = <T>(items: T[], page: number, pageSize: number): T[] =>
  items.slice((page - 1) * pageSize, page * pageSize);

/** 総ページ数。0件でも1ページとして扱い、ページ番号を空にしません。 */
export const totalPageCount = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / pageSize));

/** ダウンロードする1ファイル分の内容。 */
export type CsvExportFile = {
  name: string;
  content: string;
};

const relationChildCodes = (menu: MosPosMenu): string[] => [
  ...menu.set_configs.flatMap((config) => [
    ...config.included_items.map((item) => item.mets_menu_cd),
    ...config.choice_groups.flatMap((group) =>
      group.selectable_items.map((item) => item.mets_menu_cd)
    ),
  ]),
  ...menu.accessories.map((accessory) => accessory.mets_menu_cd),
];

/** 選択された root から、RELATION が参照する MASTER を再帰的に集める。 */
const exportClosure = (
  selected: MosPosMenu[],
  allMenus: MosPosMenu[]
): MosPosMenu[] => {
  const byCode = new Map(allMenus.map((menu) => [menu.mets_menu_cd, menu]));
  const included = new Map(selected.map((menu) => [menu.mets_menu_cd, menu]));
  const queue = [...selected];

  while (queue.length > 0) {
    const current = queue.shift() as MosPosMenu;
    relationChildCodes(current).forEach((code) => {
      const child = byCode.get(code);
      if (child && !included.has(code)) {
        included.set(code, child);
        queue.push(child);
      }
    });
  }
  return Array.from(included.values());
};

/**
 * SCR009 No.12 のCSVエクスポート。表示中の絞り込み結果を起点にし、再取込に必要な
 * RELATION の参照先も含めて MASTER / RELATION の2ファイルに出します。
 */
export const csvExportFiles = (
  menus: MosPosMenu[],
  businessDate: string,
  allMenus: MosPosMenu[] = menus
): CsvExportFile[] => {
  const stamp = businessDate.replace(/-/g, '');
  const exportMenus = exportClosure(menus, allMenus);
  return [
    {
      name: `MOS_POS_MENU_MASTER_${stamp}.csv`,
      content: buildMasterCsv(exportMenus),
    },
    {
      name: `MOS_POS_MENU_RELATION_${stamp}.csv`,
      content: buildRelationCsv(exportMenus),
    },
  ];
};

/** 価格表示。NOS と同じく円記号付きで出します。値が無ければハイフン。 */
export const formatYen = (value?: number): string =>
  value === undefined ? '-' : `¥${value.toLocaleString('en-US')}`;

/** ファイルサイズ表示。KB 単位に丸めます。0 KB とは出しません。 */
export const formatFileSize = (bytes: number): string =>
  `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** バリデーションの段階。SCR010 No.5（フォーマット）と No.6（参照整合性）を分けて出すため。 */
export type FindingPhase = 'format' | 'reference';

/**
 * ルールIDから段階を判定します。R-xxx が参照整合性、それ以外（V-xxx と
 * 行数・サイズ上限）はファイルを読んだ時点の検査なのでフォーマット側に寄せます。
 */
export const findingPhase = (rule: string): FindingPhase =>
  rule.startsWith('R-') ? 'reference' : 'format';
